import { useEffect, useRef } from 'react';
import useMotionKit from '../../../hooks/useMotionKit';
import { useAppMotion } from '../../../hooks/useAppMotion';

/**
 * Baut eine Motion-Kit-Szene in einen leeren Container, sobald das Kit
 * geladen ist, und raeumt sie restlos wieder ab.
 *
 *   const { ref, ready } = useKitScene(({ EO, gsap, ScrollTrigger, root, reduced }) => {
 *     const card = EO.ui.profileCard({...});
 *     root.append(card);
 *     const tl = gsap.timeline(...);
 *     return () => { ... }; // optional, zusaetzliches Aufraeumen
 *   }, [isDesktop]);
 *   <div ref={ref} className="eo" />
 *
 * - Alle Tweens und ScrollTrigger aus `setup` laufen in einem gsap.context und
 *   werden beim Abbau per revert() zurueckgenommen (React StrictMode baut
 *   Effekte doppelt auf — ohne revert stuenden Szenen doppelt da).
 * - Der Container darf KEINE React-Kinder haben: Beim Abbau wird er geleert.
 * - `reduced` (Bewegung reduzieren): setup soll dann den Endzustand zeigen,
 *   ohne Scroll-Kamera und ohne Dauerschleifen.
 * - `enabled: false` haelt den Aufbau zurueck (z. B. bis die Szene in die
 *   Naehe des Bildschirms kommt, siehe useNearViewport) — spart Rechenzeit
 *   beim Laden, vor allem auf dem Telefon.
 */
const useKitScene = (setup, deps = [], { enabled = true } = {}) => {
  const ref = useRef(null);
  const kit = useMotionKit();
  const { reduced } = useAppMotion();

  useEffect(() => {
    const root = ref.current;
    if (!kit || !root || !enabled) return undefined;
    const { gsap, ScrollTrigger, EO } = kit;
    let extra;
    const ctx = gsap.context(() => {
      extra = setup({ EO, gsap, ScrollTrigger, root, reduced: Boolean(reduced) });
    }, root);
    // Nach dem Einhaengen neue Hoehen beruecksichtigen (Pins, Start/Ende).
    ScrollTrigger.refresh();
    return () => {
      if (typeof extra === 'function') extra();
      ctx.revert();
      root.replaceChildren();
    };
    // setup bewusst nicht in den Abhaengigkeiten: Es ist bei jedem Rendern eine
    // neue Funktion; neu aufgebaut wird nur bei Kit, Bewegungswunsch und deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kit, reduced, enabled, ...deps]);

  return { ref, ready: Boolean(kit) && enabled };
};

export default useKitScene;
